<script setup>
import { computed, inject, watch } from 'vue'
import { useAnalyzer } from '#/analyzer/useAnalyzer'
import AppButton from '#/general/AppButton'
import { useFilters } from '#/general/useFilters'
import PersistentTooltip from '#/general/PersistentTooltip'
import QuickViewField from '#/analyzer/QuickViewField'
import AppDialog from '#/general/AppDialog'
import B2CQuickViewChart from 'components/analyzer/B2CQuickViewChart.vue'

const { global, isProduction, isLoggedIn, pages, screen, showB2CQuestionnairePopup } = inject('Shared')
const { prettifyNumber } = useFilters()
const {
  searchText,
  selectedPotential,
  showQuickView,
  selectedSearchResult,
  findPotentialEnabled,
  marker,
  mapObj,
  searchAddressesEnabled,
  markerLayerLoaded
} = useAnalyzer()

watch(showQuickView, v => {
  if (!v) {
    const prevLatLong = [selectedPotential.value.long, selectedPotential.value.lat]
    selectedPotential.value = null
    if (marker.value) {
      marker.value.remove()
    }
    if (markerLayerLoaded.value) {
      try {
        if (mapObj.value.getStyle().layers.some(e => e.id === 'highlightedSelectedPotential')) { mapObj.value.removeLayer('highlightedSelectedPotential') }
        if (mapObj.value.getStyle().layers.some(e => e.id === 'fillSelectedPotential')) { mapObj.value.removeLayer('fillSelectedPotential') }
        if (mapObj.value.getStyle().sources.selectedPotentialCoordinates) { mapObj.value.removeSource('selectedPotentialCoordinates') }
      } catch (e) {
        console.log('Didnt find layer or source to remove, it might indicate an issue in the logic.')
      }
      markerLayerLoaded.value = false
    }
    searchText.value = null
    searchAddressesEnabled.value = true
    findPotentialEnabled.value = false
    if (mapObj.value.getZoom() === 16) {
      mapObj.value.flyTo({ center: prevLatLong, zoom: 15, curve: 1, essential: true })
    }
  } else {
    selectedSearchResult.value = { address: null, addressId: null }
  }
})

async function submitClicked () {
  showB2CQuestionnairePopup.value = true
}
const splitAddress = computed(() => {
  const address = selectedPotential.value.searchedAddress ? selectedPotential.value.searchedAddress : selectedPotential.value.buildings[0].address
  const index = address.match(/\d{4}/).index - 1
  return [address.slice(0, index - 1), address.slice(index + 1)]
})
</script>

<template lang="pug">
app-dialog(v-model:show-modal="showQuickView" seamless :transition="screen.gt.xs ? 'float' : 'fade'" :position="screen.gt.xs ? 'right' : 'bottom'" :full-width="screen.lt.xs")
  template(#modal-content)
    .analyzerB2CQuickViewContainer(v-if="selectedPotential")
      .containerContent
        .b2cCloseBtn(@click="showQuickView = false" :style="`background: ${global.graphicElementColor}`")
          svg.gt-xs(width='11' height='11' viewBox='0 0 11 11' fill='none' xmlns='http://www.w3.org/2000/svg')
            path(fill-rule='evenodd' clip-rule='evenodd' d='M2.31893 0.196697L0.197607 2.31802L3.37958 5.5L0.197602 8.68198L2.31892 10.8033L5.50091 7.62132L8.68289 10.8033L10.8042 8.68198L7.62223 5.5L10.8042 2.31802L8.68289 0.196695L5.50091 3.37867L2.31893 0.196697Z' fill='#F6F7FA')
          svg.lt-sm(width='15' height='15' viewBox='0 0 15 15' fill='none' xmlns='http://www.w3.org/2000/svg')
            path(d='M14.1265 1.42309L12.7038 0L7.06325 5.64191L1.42274 0L0 1.42309L5.64051 7.065L0 12.7069L1.42274 14.13L7.06325 8.48809L12.7038 14.13L14.1265 12.7069L8.48599 7.065L14.1265 1.42309Z' fill='#626C85')

        .potentialAddress {{ splitAddress[0] }}
          .span {{ splitAddress[1] }}
        quick-view-field(title='Parzellen-Nummer:' )
          .row.flex.items-center
            .dataTitle.q-pr-sm {{ selectedPotential.parcel.parcelNr ? selectedPotential.parcel.parcelNr : 'Unknown' }}
            persistent-tooltip(:content-width="`${screen.gt.xs ? 250 : (200)}px`" :show-info-circle="true" title="Parzellen-Nummer:" body="Die eindeutige Parzellen-Nummer der Gemeinde, in welcher sich die abgefragte Liegenschaft befindet.")
              template(#icon)
                .iconHelperContainer
                  svg(width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg')
                    path(d='M6.176 4.4H8.336L7.976 7.856H6.536L6.176 4.4ZM7.256 10.08C6.94667 10.08 6.69333 9.98667 6.496 9.8C6.29867 9.61333 6.2 9.38667 6.2 9.12C6.2 8.848 6.29867 8.624 6.496 8.448C6.69333 8.26667 6.94667 8.176 7.256 8.176C7.56533 8.176 7.81867 8.26667 8.016 8.448C8.21333 8.624 8.312 8.848 8.312 9.12C8.312 9.38667 8.21333 9.61333 8.016 9.8C7.81867 9.98667 7.56533 10.08 7.256 10.08Z' fill='#EB604F')
                    circle(cx='7' cy='7' r='6.4' stroke='#EB604F' stroke-width='1.2')
                    circle(cx='7' cy='7' r='6.4' stroke='#EB604F' stroke-width='1.2')
        quick-view-field(title='Grundstücksfläche:')
          .dataTitle
            span.relative-position
              span {{ prettifyNumber(selectedPotential.parcel.area, 1) }}m
              .smallText 2
        .charts--container
          .chart
            b2-c-quick-view-chart(:value="60" index="1" :potential-id="selectedPotential._id")
            .chart--title Sanierungsdruck
          .chart
            b2-c-quick-view-chart(:value="80" index="2" :potential-id="selectedPotential._id")
            .chart--title Entwicklungspotenzial
        .questionnaireBtnContainer
          app-button.questionnaireBtn(label="Jetzt Potenzial anzeigen" @clicked="submitClicked")
</template>

<style lang="sass">
.position-right
  right: 50px !important
.position-bottom
  bottom: -450px !important
.questionnaireBtnContainer
  width: 100%
  display: flex
  align-items: center
  justify-content: center
  .questionnaireBtn
    width: 80% !important
    max-width: 414px !important
    border-radius: 6px !important
    .label
      font-size: 17px !important
    @media screen and (min-width: 600px)
    @media screen and (max-width: 599px)
.analyzerB2CQuickViewContainer
  position: relative
  max-width: 500px
  @media screen and (min-width: 1024px)
    padding: 13px 20px 25px 0px
  @media screen and (min-width: 600px) and (max-width: 1023px)
    padding: 20px 20px 30px 20px
  @media screen and (max-width: 599px)
    align-self: flex-end
    display: flex
    align-items: center
    border-radius: 10px
    margin: 0 20px 0px 20px
    transition: all .5s
  .containerContent
    background: white
    border: 1px solid #D9D9D9
    border-top: 0
    box-sizing: border-box
    box-shadow: 7px 3px 20px rgba(239, 239, 239, 0.3)
    position: relative
    display: flex
    flex-direction: column
    @media screen and (min-width: 600px)
      padding: 30px
      border-radius: 6px
      width: 500px
      max-width: 100vw
    @media screen and (max-width: 599px)
      padding: 15px
      margin-bottom: 34px
      height: 100%
      width: 100%
      border-radius: 10px
    .dataTitle
      color: v-bind('global.graphicElementColor')
      font-size: 14px
      font-style: normal
      font-weight: 500
      line-height: 20px
      letter-spacing: 0px
      text-align: left
      position: relative
      @media screen and (max-width: 599px)
        display: flex
        align-items: center
      .endBrace
        margin-left: 8px
      .smallText
        position: absolute
        z-index: 5
        top: -4px
        right: -7px
        font-size: 10px
    .potentialText
      font-style: normal
      font-weight: 500
      font-size: 10px
      line-height: 200%
      color: #007BFF
      position: relative

    .potentialAddress
      font-style: normal
      font-weight: 700
      letter-spacing: 0
      padding-right: 16px
      @media screen and (max-width: 1023px)
        margin-bottom: 20px
      @media screen and (min-width: 600px)
        font-size: 24px
        line-height: 30px
      @media screen and (max-width: 599px)
        font-size: 20px
    .itemValue
      font-style: normal
      color: v-bind('global.graphicElementColor')
      position: relative
      @media screen and (min-width: 1024px)
        line-height: 130%
        font-size: 16px
        font-weight: 500
      @media screen and (max-width: 1023px)
        font-size: 14px
        font-weight: 600
      .smallText
        position: absolute
        top: -4px
        right: -7px
        font-size: 10px
    .charts--container
      @media screen and (min-width: 600px)
        display: flex
        justify-content: space-between
        margin: 40px 20px 43px
      @media screen and (max-width: 600px)
        margin: 10px 20px 23px
      .chart
        .chart--title
          margin-top: 20px
          font-weight: 500
          font-size: 16px
          line-height: 20px
          color: #626C85
          text-align: center
          @media screen and (max-width: 600px)
            margin-bottom: 20px
    .b2cCloseBtn
      z-index: 4
      cursor: pointer
      display: flex
      justify-content: center
      align-items: center
      width: 25px
      height: 25px
      position: absolute
      @media screen and (min-width: 600px)
        right: 18px
        top: 18px
        box-shadow: 0px 4px 16px rgba(98, 108, 133, 0.16)
        border-radius: 6px
      @media screen and (max-width: 599px)
        right: 10px
        top: 10px
        background: transparent !important
</style>
